import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import withShipment from '../../withShipment';
import { userInfoSelector } from '../../redux/selectors/auth';
import {
    accountsSelector,
    trackerLoadedSelector,
} from '../../redux/selectors/businessTracker';
import {
    listTrackerAccounts,
    saveTrackerAccount,
    deleteTrackerAccount,
    saveTrackerCell,
} from '../../redux/actions/businessTracker';
import { TRACKERS } from './config/trackers';
import { BELONGS_META } from './config/belongs';
import CustomToolbar from '../global/CustomToolbar';
import Dashboard from './Dashboard';
import TrackerDetail from './TrackerDetail';
import BelongsView from './BelongsView';
import './BusinessTracker.css';

// Screen state machine. Only one screen is mounted at a time; chrome is global.
const SCREENS = {
    DASHBOARD: 'dashboard',
    TRACKER: 'tracker',
    BELONGS: 'belongs',
};

function getLocationId(userInfo) {
    return userInfo?.company || userInfo?.companyId || null;
}

function BusinessTrackerBase({
    userInfo,
    accounts,
    loaded,
    listTrackerAccounts: listAction,
    saveTrackerAccount: saveAction,
    deleteTrackerAccount: deleteAction,
    saveTrackerCell: saveCellAction,
}) {
    const locationId = useMemo(() => getLocationId(userInfo), [userInfo]);
    const [screen, setScreen] = useState(SCREENS.DASHBOARD);
    const [ctx, setCtx] = useState({
        trackerKey: null,
        belongsFilter: 'all',
        belongsKey: null,
    });
    const [toasts, setToasts] = useState([]);

    useEffect(() => {
        if (locationId) listAction({ locationId });
    }, [locationId, listAction]);

    // Toast: auto-expire each toast individually after 2.5s.
    useEffect(() => {
        if (toasts.length === 0) return undefined;
        const id = toasts[0].id;
        const timer = setTimeout(
            () => setToasts((prev) => prev.filter((t) => t.id !== id)),
            2500,
        );
        return () => clearTimeout(timer);
    }, [toasts]);

    const showToast = useCallback((message, kind = 'info') => {
        if (!message) return;
        setToasts((prev) => [
            ...prev,
            { id: Date.now() + Math.random(), message, kind },
        ]);
    }, []);

    const navTracker = useCallback((trackerKey, belongsFilter = 'all') => {
        setCtx({ trackerKey, belongsFilter, belongsKey: null });
        setScreen(SCREENS.TRACKER);
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
    }, []);

    const navBelongs = useCallback((belongsKey) => {
        setCtx({ trackerKey: null, belongsFilter: 'all', belongsKey });
        setScreen(SCREENS.BELONGS);
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
    }, []);

    const navDashboard = useCallback(() => {
        setCtx({ trackerKey: null, belongsFilter: 'all', belongsKey: null });
        setScreen(SCREENS.DASHBOARD);
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
    }, []);

    const api = useMemo(() => ({
        saveAccount: (payload, cb) => {
            saveAction({ locationId, ...payload }, (err, msg) => {
                if (!err) showToast(msg || 'Saved', 'success');
                else showToast(msg || 'Save failed', 'error');
                if (cb) cb(err, msg);
            });
        },
        deleteAccount: (accountId, cb) => {
            deleteAction({ locationId, accountId }, (err, msg) => {
                if (!err) showToast(msg || 'Cancelled', 'success');
                else showToast(msg || 'Delete failed', 'error');
                if (cb) cb(err, msg);
            });
        },
        saveCell: (payload, cb) => {
            saveCellAction({ locationId, ...payload }, (err, msg) => {
                if (!err) showToast(msg || 'Saved', 'success');
                else if (msg) showToast(msg, 'error');
                if (cb) cb(err, msg);
            });
        },
    }), [locationId, saveAction, deleteAction, saveCellAction, showToast]);

    // Toolbar title reflects the active screen. Back arrow is shown on
    // tracker / belongs screens (CustomToolbar's creatingOrder mode).
    const isSubScreen = screen === SCREENS.TRACKER || screen === SCREENS.BELONGS;
    const toolbarTitle = useMemo(() => {
        if (screen === SCREENS.DASHBOARD) return 'Business Trackers';
        if (screen === SCREENS.TRACKER) {
            const t = TRACKERS[ctx.trackerKey];
            return t ? `${t.label} Accounts` : 'Tracker';
        }
        if (screen === SCREENS.BELONGS) {
            const meta = BELONGS_META[ctx.belongsKey];
            return meta ? meta.label : 'Category';
        }
        return 'Business Trackers';
    }, [screen, ctx.trackerKey, ctx.belongsKey]);

    const renderToolbar = () => (
        <CustomToolbar
            title={toolbarTitle}
            creatingOrder={isSubScreen}
            backTitle="Back to Dashboard"
            handleClose={navDashboard}
            customIcons={isSubScreen ? undefined : <Fragment />}
        />
    );

    if (!locationId) {
        return (
            <div className="bt-root">
                {renderToolbar()}
                <div className="bt-frame">
                    <div className="bt-empty">
                        Loading user context… If this persists, please re-login.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bt-root">
            {renderToolbar()}
            <div className="bt-frame">
                {screen === SCREENS.DASHBOARD && (
                    <Dashboard
                        accounts={accounts}
                        loaded={loaded}
                        onOpenTracker={navTracker}
                        onOpenBelongs={navBelongs}
                    />
                )}
                {screen === SCREENS.TRACKER && ctx.trackerKey && (
                    <TrackerDetail
                        trackerKey={ctx.trackerKey}
                        initialBelongsFilter={ctx.belongsFilter}
                        accounts={accounts}
                        api={api}
                        showToast={showToast}
                    />
                )}
                {screen === SCREENS.BELONGS && ctx.belongsKey && (
                    <BelongsView
                        belongsKey={ctx.belongsKey}
                        accounts={accounts}
                        onOpenTracker={navTracker}
                    />
                )}
            </div>
            <ToastContainer toasts={toasts} />
        </div>
    );
}

function ToastContainer({ toasts }) {
    if (!toasts.length) return null;
    return (
        <div className="bt-toast-container" role="status" aria-live="polite">
            {toasts.map((t) => (
                <div key={t.id} className={`bt-toast ${t.kind || 'info'}`}>
                    <span role="img" aria-label={t.kind || 'info'}>
                        {t.kind === 'success' ? '✓' : t.kind === 'error' ? '⚠' : 'ℹ'}
                    </span>
                    <span>{t.message}</span>
                </div>
            ))}
        </div>
    );
}

export default withShipment(
    {
        mapStateToProps: (state) => ({
            userInfo: userInfoSelector(state),
            accounts: accountsSelector(state),
            loaded: trackerLoadedSelector(state),
        }),
        actionCreators: {
            listTrackerAccounts,
            saveTrackerAccount,
            deleteTrackerAccount,
            saveTrackerCell,
        },
    },
    BusinessTrackerBase,
);
